import { Tooltip } from '@forethought-technologies/forethought-elements';
import { CsatButton } from 'components/TextInput/CsatButton';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  selectCsatUiState,
  selectCustomConfigParams,
  selectFeatureFlags,
  selectIsLiveChatMode,
  selectIsRestartDisabled,
  selectIsSolveConfigPreview,
} from 'reducers/workflows';
import { ModalHeaderActionButton, ModalHideActionButton } from './styles';
import {
  selectLegibleTextColor,
  selectWidgetArticleConfig,
} from 'reducers/config';
import {
  IconMessageX,
  IconMinus,
  IconReload,
  IconX,
  IconSearch,
} from '@tabler/icons-react';
import {
  selectLiveChatStatus,
  setIsEndLiveChatModalVisible,
} from 'reducers/live-chat';
import {
  modalMinimizeWidget,
  modalRestartConversation,
} from 'actions/workflows-v2/workflowsV2';
import { postMessage } from 'utils/postMessage';
import { selectWidgetStyle } from 'reducers/main';
import { KNOWLEDGE_SEARCH_PAGE } from 'constants/pageConsts';
import { navigateTo } from 'actions/main';
import {
  selectLiveChatIntegration,
  selectUserEndedChat,
} from 'reducers/live-chat/index';

interface ModalActionsProps {
  color?: string;
  size?: number;
}

export const ModalActions = ({ color, size = 20 }: ModalActionsProps) => {
  const dispatch = useAppDispatch();
  const csatUiState = useAppSelector(selectCsatUiState);
  const isLiveChatMode = useAppSelector(selectIsLiveChatMode);
  const liveChatIntegration = useAppSelector(selectLiveChatIntegration);
  const legibleTextColor = useAppSelector(selectLegibleTextColor);
  const isRestartDisabled = useAppSelector(selectIsRestartDisabled);
  const customConfigParams = useAppSelector(selectCustomConfigParams);
  const featureFlags = useAppSelector(selectFeatureFlags);
  const isSolveConfigPreview = useAppSelector(selectIsSolveConfigPreview);
  const widgetStyle = useAppSelector(selectWidgetStyle);
  const userEndedChat = useAppSelector(selectUserEndedChat);
  const widgetArticleSearchConfig = useAppSelector(selectWidgetArticleConfig);
  const liveChatStatus = useAppSelector(selectLiveChatStatus);
  const isEndedStatus = liveChatStatus === 'ended';

  const disableClose = customConfigParams?.disable_close === 'true';
  const disableXButton = customConfigParams?.disable_x_button === 'true';
  const isCloseButtonEnabled = featureFlags.includes(
    'show_close_button_on_widget',
  );
  const isSolveUiTabsEnabled = featureFlags.includes('solve_ui_tabs');
  const actionButtonColor = color || legibleTextColor;

  const widgetEndLiveChat = async () => {
    if (csatUiState.visible) {
      return;
    }

    dispatch(setIsEndLiveChatModalVisible(true));
  };

  return (
    <>
      {csatUiState.show && <CsatButton color={color} size={size} />}
      {widgetArticleSearchConfig.enabled && !isSolveUiTabsEnabled && (
        <Tooltip tooltipContent='Knowledge'>
          <ModalHeaderActionButton
            aria-label='Knowledge'
            onClick={() => {
              dispatch(navigateTo(KNOWLEDGE_SEARCH_PAGE));
            }}
          >
            <IconSearch color={actionButtonColor} size={size} />
          </ModalHeaderActionButton>
        </Tooltip>
      )}
      {isLiveChatMode ? (
        isEndedStatus ||
        (userEndedChat && liveChatIntegration === 'sunco') ? null : (
          <Tooltip tooltipContent='End chat with agent'>
            <ModalHeaderActionButton
              aria-label='End chat with agent'
              onClick={widgetEndLiveChat}
            >
              <IconMessageX color={actionButtonColor} size={size} />
            </ModalHeaderActionButton>
          </Tooltip>
        )
      ) : (
        <Tooltip tooltipContent='Restart chat'>
          <ModalHeaderActionButton
            aria-disabled={isRestartDisabled}
            aria-label='Restart chat'
            disabled={isRestartDisabled}
            onClick={() => {
              dispatch(modalRestartConversation());
            }}
          >
            <IconReload color={actionButtonColor} size={size} />
          </ModalHeaderActionButton>
        </Tooltip>
      )}
      {!disableClose && widgetStyle !== 'floating_bar' && (
        <div>
          <Tooltip tooltipContent='Minimize chat'>
            <ModalHideActionButton
              aria-label='Minimize chat'
              onClick={() => {
                dispatch(modalMinimizeWidget());
              }}
            >
              <IconMinus color={actionButtonColor} size={size} />
            </ModalHideActionButton>
          </Tooltip>
        </div>
      )}
      {isCloseButtonEnabled && !isSolveConfigPreview && !disableXButton && (
        <Tooltip tooltipContent='Close chat'>
          <ModalHeaderActionButton
            aria-label='Close chat'
            onClick={() => {
              postMessage({ event: 'hideSolveWidget' });
            }}
          >
            <IconX color={actionButtonColor} size={size} />
          </ModalHeaderActionButton>
        </Tooltip>
      )}
    </>
  );
};
